<template>
  <div class="admin-section">
    <div class="section-header">
      <h2 class="section-title">All Studies</h2>
      <div style="display: flex; gap: 0.5rem;">
        <Button
          @click="showCreateStudyForm = !showCreateStudyForm"
          variant="default"
          size="sm"
          class="refresh-btn"
        >
          {{ showCreateStudyForm ? 'Cancel' : 'Create Study' }}
        </Button>
        <Button
          @click="loadStudies"
          :disabled="isLoadingStudies"
          variant="default"
          size="sm"
          class="refresh-btn"
        >
          <span v-if="!isLoadingStudies">Refresh</span>
          <span v-else>Loading...</span>
        </Button>
      </div>
    </div>

    <!-- Filters: search, ClinicalTrials.gov toggle -->
    <div class="studies-toolbar">
      <div class="toolbar-search">
        <Input
          v-model="searchQuery"
          placeholder="Search by name or keywords"
          @keyup.enter="applyFilters"
        />
        <Button
          @click="applyFilters"
          :disabled="isLoadingStudies"
          variant="default"
          size="sm"
        >
          Search
        </Button>
        <Button
          v-if="searchQuery || includeBulk"
          @click="clearFilters"
          :disabled="isLoadingStudies"
          variant="default"
          size="sm"
          style="background-color: #64748b;"
        >
          Clear
        </Button>
      </div>
      <label class="checkbox-label toolbar-toggle">
        <input
          type="checkbox"
          v-model="includeBulk"
          class="checkbox-input"
          @change="applyFilters"
        />
        <span>Include ClinicalTrials.gov studies</span>
      </label>
    </div>

    <!-- Study counts -->
    <div class="studies-count-info">
      {{ studyCounts.matching }} result{{ studyCounts.matching === 1 ? '' : 's' }}
      · {{ studyCounts.total }} total stud{{ studyCounts.total === 1 ? 'y' : 'ies' }} in database
    </div>

    <!-- Create/Edit Study Form -->
    <div v-if="showCreateStudyForm || editingStudy" class="study-form-container">
      <h3 class="form-title">{{ editingStudy ? 'Edit Study' : 'Create New Study' }}</h3>
      <div v-if="studyError" class="message error">
        {{ studyError }}
      </div>
      <form @submit.prevent="editingStudy ? updateStudy() : createStudy()" class="study-form">
        <div class="form-group">
          <Label for="study-name">Name *</Label>
          <Input
            id="study-name"
            v-model="studyForm.name"
            placeholder="Study name"
            required
          />
        </div>
        <div class="form-group">
          <Label for="study-description">Description *</Label>
          <textarea
            id="study-description"
            v-model="studyForm.description"
            placeholder="Study description"
            required
            class="form-textarea"
            rows="4"
          ></textarea>
        </div>
        <div class="form-group">
          <Label for="study-inclusion">Inclusion Criteria</Label>
          <textarea
            id="study-inclusion"
            v-model="studyForm.inclusion_criteria"
            placeholder="Inclusion criteria"
            class="form-textarea"
            rows="3"
          ></textarea>
        </div>
        <div class="form-group">
          <Label for="study-exclusion">Exclusion Criteria</Label>
          <textarea
            id="study-exclusion"
            v-model="studyForm.exclusion_criteria"
            placeholder="Exclusion criteria"
            class="form-textarea"
            rows="3"
          ></textarea>
        </div>
        <div class="form-group">
          <Label for="study-procedures">Study Procedures</Label>
          <textarea
            id="study-procedures"
            v-model="studyForm.study_procedures"
            placeholder="Study procedures"
            class="form-textarea"
            rows="3"
          ></textarea>
        </div>
        <div class="form-group">
          <Label for="study-keywords">Keywords</Label>
          <Input
            id="study-keywords"
            v-model="studyForm.keywords"
            placeholder="Comma-separated keywords"
          />
        </div>
        <div class="form-group">
          <label class="checkbox-label">
            <input
              type="checkbox"
              v-model="studyForm.is_active"
              class="checkbox-input"
            />
            <span>Active</span>
          </label>
        </div>
        <div class="form-actions">
          <Button
            type="submit"
            :disabled="isSavingStudy"
            variant="default"
            size="sm"
          >
            <span v-if="!isSavingStudy">{{ editingStudy ? 'Update' : 'Create' }}</span>
            <span v-else>Saving...</span>
          </Button>
          <Button
            type="button"
            @click="cancelStudyForm"
            variant="default"
            size="sm"
            style="background-color: #64748b;"
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>

    <div v-if="studiesError" class="message error">
      {{ studiesError }}
    </div>

    <div v-if="isLoadingStudies && studies.length === 0" class="loading-state">
      Loading studies...
    </div>

    <div v-else-if="studies.length === 0" class="empty-state">
      No studies found.
    </div>

    <div v-else class="studies-list">
      <div
        v-for="study in studies"
        :key="study.id"
        class="study-item"
      >
        <div class="study-header">
          <div class="study-info" @click="toggleStudy(study.id)" style="cursor: pointer; flex: 1;">
            <div class="study-name">{{ study.name }}</div>
            <div class="study-meta">
              <span class="study-status" :class="{ active: study.is_active, inactive: !study.is_active }">
                {{ study.is_active ? 'Active' : 'Inactive' }}
              </span>
              <span v-if="study.source" class="study-source" :class="study.source.toLowerCase()">
                {{ study.source }}
              </span>
              <!-- <span v-if="study.sponsor" class="study-sponsor">{{ study.sponsor }}</span>
              <span v-if="study.nct_id" class="study-nctid">
                <a :href="`https://clinicaltrials.gov/ct2/show/${study.nct_id}`" target="_blank" @click.stop>NCT: {{ study.nct_id }}</a>
              </span> -->
              <span class="study-date">{{ formatDate(study.created_at) }}</span>
            </div>
          </div>
          <div class="study-actions">
            <Button
              @click.stop="editStudy(study)"
              :disabled="isSavingStudy"
              variant="default"
              size="sm"
              style="background-color: #3b82f6; margin-right: 0.5rem;"
            >
              Edit
            </Button>
            <Button
              @click.stop="deleteStudy(study)"
              :disabled="isDeletingStudy[study.id]"
              variant="destructive"
              size="sm"
            >
              <span v-if="!isDeletingStudy[study.id]">Delete</span>
              <span v-else>Deleting...</span>
            </Button>
            <span class="expand-icon" :class="{ expanded: expandedStudies[study.id] }" @click.stop="toggleStudy(study.id)">
              ▼
            </span>
          </div>
        </div>

        <div v-if="expandedStudies[study.id]" class="study-details">
          <!-- Move Assigned Patients HERE - before metadata -->
          <details @toggle="togglePatients(study.id, $event)" style="margin-top: 1rem;">
            <summary class="patients-header" style="outline: none; cursor: pointer;">
              <span class="expand-icon" :class="{ expanded: expandedPatients[study.id] }">
                ▼
              </span>
              <h3 class="patients-title">Assigned Patients</h3>
            </summary>
            <div v-if="loadingPatients[study.id]" class="loading-patients">
              Loading patients...
            </div>
            <div v-else-if="studyPatients[study.id]?.length === 0" class="no-patients">
              No patients assigned to this study yet.
            </div>
            <div v-else class="patients-list">
              <div
                v-for="patient in studyPatients[study.id]"
                :key="patient.id"
                class="patient-item"
              >
                <div class="patient-info">
                  <div class="patient-email">{{ patient.email }}</div>
                </div>
              </div>
            </div>
          </details>

          <!-- Then the metadata section -->
          <div v-if="study.id || study.source || study.sponsor || study.nct_id" class="detail-section study-metadata">
            <strong>Study Metadata:</strong>
            <div class="metadata-grid">
              <div v-if="study.id">
                <span class="metadata-label">WSR ID:</span>
                <span class="metadata-value">{{ study.id }}</span>
              </div>
              <div v-if="study.source">
                <span class="metadata-label">Source:</span>
                <span class="metadata-value">{{ study.source }}</span>
              </div>
              <div v-if="study.sponsor">
                <span class="metadata-label">Sponsor:</span>
                <span class="metadata-value">{{ study.sponsor }}</span>
              </div>
              <div v-if="study.nct_id">
                <span class="metadata-label">NCT ID:</span>
                <span class="metadata-value">
                  <a :href="`https://clinicaltrials.gov/ct2/show/${study.nct_id}`" target="_blank">{{ study.nct_id }}</a>
                </span>
              </div>
            </div>
          </div>
          <div class="detail-section">
            <strong>Description:</strong>
            <p>{{ study.description }}</p>
          </div>
          <div v-if="study.inclusion_criteria" class="detail-section">
            <strong>Inclusion Criteria:</strong>
            <p>{{ study.inclusion_criteria }}</p>
          </div>
          <div v-if="study.exclusion_criteria" class="detail-section">
            <strong>Exclusion Criteria:</strong>
            <p>{{ study.exclusion_criteria }}</p>
          </div>
          <div v-if="study.study_procedures" class="detail-section">
            <strong>Study Procedures:</strong>
            <p>{{ study.study_procedures }}</p>
          </div>
          <div v-if="study.keywords" class="detail-section">
            <strong>Keywords:</strong>
            <p>{{ study.keywords }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div
      v-if="!isLoadingStudies && (page > 0 || hasMore)"
      class="studies-pagination"
    >
      <Button
        @click="prevPage"
        :disabled="page === 0 || isLoadingStudies"
        variant="default"
        size="sm"
      >
        Previous
      </Button>
      <span class="page-indicator">Page {{ page + 1 }}</span>
      <Button
        @click="nextPage"
        :disabled="!hasMore || isLoadingStudies"
        variant="default"
        size="sm"
      >
        Next
      </Button>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { API_ENDPOINTS, HTTP_STATUS, ROUTES, ERROR_MESSAGES } from '@/constants'

export default {
  name: 'StudiesManagement',
  components: {
    Button,
    Input,
    Label
  },
  props: {
    isAuthenticated: {
      type: Boolean,
      required: true
    },
    isAdmin: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      studies: [],
      isLoadingStudies: false,
      studiesError: '',
      showCreateStudyForm: false,
      editingStudy: null,
      isSavingStudy: false,
      studyError: '',
      isDeletingStudy: {},
      expandedStudies: {},
      expandedPatients: {},
      studyPatients: {},
      loadingPatients: {},
      // Filters / pagination for the studies list. Bulk ClinicalTrials.gov
      // studies are excluded by default so the curated catalog stays focused;
      // toggling includeBulk (and/or searching) surfaces the synced set, which
      // is paginated because it can contain tens of thousands of rows.
      includeBulk: false,
      searchQuery: '',
      page: 0,
      pageSize: 50,
      hasMore: false,
      // Counts shown below the search bar: matching = current filter/search,
      // total = every study row in the database.
      studyCounts: { total: 0, matching: 0 },
      studyForm: {
        name: '',
        description: '',
        inclusion_criteria: '',
        exclusion_criteria: '',
        study_procedures: '',
        keywords: '',
        is_active: true
      }
    }
  },
  mounted() {
    this.loadStudies()
  },
  methods: {
    async loadStudies() {
      if (!this.isAuthenticated || !this.isAdmin) {
        return
      }

      this.isLoadingStudies = true
      this.studiesError = ''

      try {
        const params = {
          include_bulk: this.includeBulk,
          limit: this.pageSize,
          offset: this.page * this.pageSize
        }
        const trimmedSearch = this.searchQuery && this.searchQuery.trim()
        if (trimmedSearch) {
          params.search = trimmedSearch
        }
        const response = await api.get(API_ENDPOINTS.ADMIN_STUDIES, { params })
        this.studies = response.data || []
        // The endpoint returns a plain array (no total count), so a full page
        // implies there may be more rows to fetch.
        this.hasMore = this.studies.length === this.pageSize
        // Refresh the counts shown below the search bar (uses the same filters,
        // without paging).
        this.fetchStudyCounts()
      } catch (error) {
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.$store.dispatch('auth/logout')
          this.$router.push(ROUTES.HOME)
          return
        }
        this.studiesError = error.response?.data?.detail || error.response?.data?.message || ERROR_MESSAGES.GENERIC_ERROR
      } finally {
        this.isLoadingStudies = false
      }
    },
    applyFilters() {
      // Reset to the first page whenever the search text or bulk toggle changes.
      this.page = 0
      this.loadStudies()
    },
    clearFilters() {
      this.searchQuery = ''
      this.includeBulk = false
      this.page = 0
      this.loadStudies()
    },
    async fetchStudyCounts() {
      try {
        const params = { include_bulk: this.includeBulk }
        const trimmedSearch = this.searchQuery && this.searchQuery.trim()
        if (trimmedSearch) {
          params.search = trimmedSearch
        }
        const response = await api.get(API_ENDPOINTS.ADMIN_STUDIES_COUNT, { params })
        this.studyCounts = {
          total: response.data?.total ?? 0,
          matching: response.data?.matching ?? 0
        }
      } catch (error) {
        // Non-critical: leave previous counts on failure.
        console.error('Failed to fetch study counts:', error)
      }
    },
    nextPage() {
      if (!this.hasMore) {
        return
      }
      this.page += 1
      this.loadStudies()
    },
    prevPage() {
      if (this.page === 0) {
        return
      }
      this.page -= 1
      this.loadStudies()
    },
    resetStudyForm() {
      this.studyForm = {
        name: '',
        description: '',
        inclusion_criteria: '',
        exclusion_criteria: '',
        study_procedures: '',
        keywords: '',
        is_active: true
      }
      this.studyError = ''
    },
    cancelStudyForm() {
      this.showCreateStudyForm = false
      this.editingStudy = null
      this.resetStudyForm()
    },
    editStudy(study) {
      this.editingStudy = study
      this.showCreateStudyForm = false
      this.studyForm = {
        name: study.name,
        description: study.description,
        inclusion_criteria: study.inclusion_criteria || '',
        exclusion_criteria: study.exclusion_criteria || '',
        study_procedures: study.study_procedures || '',
        keywords: study.keywords || '',
        is_active: study.is_active
      }
      this.studyError = ''
      // Scroll to form
      this.$nextTick(() => {
        const formElement = document.querySelector('.study-form-container')
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    async createStudy() {
      if (!this.isAuthenticated || !this.isAdmin) {
        return
      }

      this.isSavingStudy = true
      this.studyError = ''

      try {
        const response = await api.post(API_ENDPOINTS.ADMIN_CREATE_STUDY, this.studyForm)
        this.studies.unshift(response.data)
        this.cancelStudyForm()
      } catch (error) {
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.$store.dispatch('auth/logout')
          this.$router.push(ROUTES.HOME)
          return
        }
        this.studyError = error.response?.data?.detail || error.response?.data?.message || ERROR_MESSAGES.GENERIC_ERROR
      } finally {
        this.isSavingStudy = false
      }
    },
    async updateStudy() {
      if (!this.isAuthenticated || !this.isAdmin || !this.editingStudy) {
        return
      }

      this.isSavingStudy = true
      this.studyError = ''

      try {
        const response = await api.put(
          API_ENDPOINTS.ADMIN_UPDATE_STUDY(this.editingStudy.id),
          this.studyForm
        )

        // Update study in the list
        const studyIndex = this.studies.findIndex(s => s.id === this.editingStudy.id)
        if (studyIndex !== -1) {
          this.studies[studyIndex] = response.data
        }

        this.cancelStudyForm()
      } catch (error) {
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.$store.dispatch('auth/logout')
          this.$router.push(ROUTES.HOME)
          return
        }
        this.studyError = error.response?.data?.detail || error.response?.data?.message || ERROR_MESSAGES.GENERIC_ERROR
      } finally {
        this.isSavingStudy = false
      }
    },
    async deleteStudy(study) {
      if (!this.isAuthenticated || !this.isAdmin) {
        return
      }

      if (!confirm(`Are you sure you want to delete the study "${study.name}"? This action cannot be undone.`)) {
        return
      }

      this.isDeletingStudy[study.id] = true

      try {
        await api.delete(API_ENDPOINTS.ADMIN_DELETE_STUDY(study.id))

        // Remove study from the list
        this.studies = this.studies.filter(s => s.id !== study.id)
      } catch (error) {
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.$store.dispatch('auth/logout')
          this.$router.push(ROUTES.HOME)
          return
        }
        alert(error.response?.data?.detail || error.response?.data?.message || ERROR_MESSAGES.GENERIC_ERROR)
      } finally {
        this.isDeletingStudy[study.id] = false
      }
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    toggleStudy(studyId) {
      if (this.expandedStudies[studyId]) {
        this.expandedStudies[studyId] = false
      } else {
        this.expandedStudies[studyId] = true
        this.loadStudyPatients(studyId)
      }
    },
    togglePatients(studyId, event) {
      this.expandedPatients[studyId] = event.target.open
    },
    async loadStudyPatients(studyId) {
      if (this.studyPatients[studyId]) {
        return
      }

      this.loadingPatients[studyId] = true

      try {
        const response = await api.get(API_ENDPOINTS.ADMIN_STUDY_PATIENTS(studyId))
        this.studyPatients[studyId] = response.data || []
      } catch (error) {
        if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
          this.$store.dispatch('auth/logout')
          this.$router.push(ROUTES.HOME)
          return
        }
        this.studyPatients[studyId] = []
        console.error(`Error loading patients for study ${studyId}:`, error)
      } finally {
        this.loadingPatients[studyId] = false
      }
    }
  }
}
</script>

<style scoped>
.admin-section {
  margin-bottom: 3rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.refresh-btn {
  background-color: #3b82f6;
  color: #ffffff;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.refresh-btn:hover:not(:disabled) {
  background-color: #2563eb;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.message {
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.9375rem;
}

.message.error {
  background-color: #fee2e2;
  color: #991b1b;
  border: 1px solid #ef4444;
}

.loading-state,
.empty-state {
  padding: 2rem;
  text-align: center;
  color: #64748b;
}

/* Studies Section Styles */
.study-form-container {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.form-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1rem;
}

.study-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-textarea {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
}

.form-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 1px #3b82f6;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
}

.checkbox-input {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
}

.form-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.studies-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.toolbar-search {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1 1 320px;
  min-width: 260px;
}

.toolbar-search :deep(input) {
  flex: 1;
}

.toolbar-toggle {
  font-size: 0.875rem;
  color: #475569;
  white-space: nowrap;
}

.studies-count-info {
  margin: -0.5rem 0 1rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #16a34a;
}

.studies-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
}

.page-indicator {
  font-size: 0.875rem;
  color: #475569;
}

.studies-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.study-item {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  overflow: hidden;
  background-color: #ffffff;
}

.study-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
}

.study-info {
  flex: 1;
}

.study-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.25rem;
}

.study-meta {
  display: flex;
  gap: 1rem;
  align-items: center;
  font-size: 0.875rem;
  color: #64748b;
}

.study-status {
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-weight: 500;
  text-transform: uppercase;
  font-size: 0.75rem;
}

.study-status.active {
  background-color: #d1fae5;
  color: #065f46;
}

.study-status.inactive {
  background-color: #fee2e2;
  color: #991b1b;
}

.study-date {
  color: #94a3b8;
}

.study-source {
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-weight: 500;
  text-transform: uppercase;
  font-size: 0.75rem;
}

.study-source.local {
  background-color: #e0e7ff;
  color: #4338ca;
}

.study-source.clinicaltrials_gov {
  background-color: #dbeafe;
  color: #1e40af;
}

.study-sponsor {
  color: #64748b;
  font-size: 0.875rem;
}

.study-nctid {
  font-size: 0.875rem;
}

.study-nctid a {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.study-nctid a:hover {
  text-decoration: underline;
}

.study-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.expand-icon {
  font-size: 0.75rem;
  color: #64748b;
  transition: transform 0.2s ease;
  display: inline-block;
  cursor: pointer;
  margin-left: 0.5rem;
}

.expand-icon.expanded {
  transform: rotate(180deg);
}

.study-details {
  border-top: 1px solid #e2e8f0;
  padding: 1rem;
  background-color: #f8fafc;
}

.detail-section {
  margin-bottom: 1rem;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.detail-section strong {
  display: block;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.detail-section p {
  color: #475569;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
}

.study-metadata {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  padding: 0.75rem;
}

.metadata-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.5rem;
}

.metadata-grid > div {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.metadata-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
}

.metadata-value {
  font-size: 0.875rem;
  color: #1e293b;
}

.metadata-value a {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
}

.metadata-value a:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .study-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .study-meta {
    flex-wrap: wrap;
  }

  .metadata-grid {
    flex-direction: column;
  }

  .study-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .form-actions {
    flex-direction: column;
  }
}

.patients-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 1rem;
}

.patients-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.loading-patients,
.no-patients {
  padding: 1rem;
  text-align: center;
  color: #64748b;
  font-size: 0.875rem;
}

.patients-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.patient-item {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem;
}

.patient-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.patient-email {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9375rem;
}
</style>
